import React, { useEffect, useState } from "react";
import {
  TextField,
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Container,
} from "@mui/material";
import { ContentPaste, KeyboardReturn } from "@mui/icons-material";
import AddTaskIcon from "@mui/icons-material/AddTask";
import { toast } from "react-toastify";
import { useEdges } from "reactflow";
import AddToTargetBtn from "./AddToTargetBtn";
const RdCampaignQuickContacts = ({
  campaignPost,
  setCampaignPost,
  setActiveStep,
}) => {
  const [contacts, setContacts] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [inputMode, setInputMode] = useState("single"); // 'single' or 'multi'

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };
  useEffect(() => {
    if (campaignPost.contacts.length > 0 && contacts.length < 1) {
      setContacts(campaignPost.contacts.map((item) => item.Numbers));
    }
  }, [campaignPost.contacts]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (inputMode === "single") {
        addSingleContact();
      } else {
        addMultipleContacts();
      }
    }
  };

  const addSingleContact = () => {
    const contact = inputValue.trim();
    if (contact) {
      setContacts([...contacts, contact]);
      setInputValue("");
    }
  };

  const addMultipleContacts = () => {
    const newContacts = inputValue
      .split("\n")
      .map((contact) => contact.trim())
      .filter((contact) => contact.length > 0);

    if (newContacts.length > 0) {
      setContacts([...contacts, ...newContacts]);
      setInputValue("");
    }
  };

  const removeContact = (index) => {
    const newContacts = [...contacts];
    newContacts.splice(index, 1);
    setContacts(newContacts);
  };

  const clearAll = () => {
    setContacts([]);
    setInputValue("");
  };

  const handlePaste = (e) => {
    // Auto-switch to multi mode if pasting multiple lines
    const pastedText = e.clipboardData.getData("text");
    if (pastedText.includes("\n")) {
      setInputMode("multi");
    }
  };

  function generateTargetAudience() {
    if (contacts.length < 1) {
      toast.error("Please import contacts first!");
      return;
    }
    const newContacts = contacts.map((item) => ({ Numbers: item }));
    setCampaignPost((prev) => ({ ...prev, contacts: newContacts }));
    toast.success("Contacts list added to Target!");
    setActiveStep(2);
  }

  return (
    <Container maxWidth="sm" sx={{ p: 3 }}>
      <Paper
        variant="outlined"
        sx={{ p: 3, borderRadius: 2, backgroundColor: "#f8f9fa" }}
      >
        <Typography
          variant="h5"
          gutterBottom
          sx={{ fontWeight: "bold", color: "#1976d2" }}
        >
          Contact Number Input
        </Typography>

        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <ToggleButtonGroup
            value={inputMode}
            exclusive
            onChange={(e, newMode) => setInputMode(newMode)}
            size="small"
          >
            <ToggleButton value="single">
              <Tooltip title="Add one number at a time">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <KeyboardReturn fontSize="small" />
                  <Typography variant="caption">Single</Typography>
                </Box>
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="multi">
              <Tooltip title="Paste multiple numbers at once">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ContentPaste fontSize="small" />
                  <Typography variant="caption">Multi</Typography>
                </Box>
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Typography variant="body2" sx={{ color: "#555" }}>
            {inputMode === "single"
              ? "Press Enter after each number"
              : "Paste multiple numbers separated by lines"}
          </Typography>
        </Box>

        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={6}
          variant="outlined"
          label="Enter contact numbers"
          placeholder={
            inputMode === "single"
              ? "Type a number and press Enter"
              : "Paste multiple numbers (one per line) and press Enter"
          }
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          sx={{
            backgroundColor: "white",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#1976d2",
              },
              "&:hover fieldset": {
                borderColor: "#1976d2",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#1976d2",
              },
            },
          }}
        />
        <Box mt={1} display={"flex"} justifyContent={"end"}>
          <Button size="small" variant="outlined" onClick={clearAll}>
            Clear All
          </Button>
        </Box>
        <Typography
          variant="subtitle1"
          sx={{ mt: 2, mb: 1, fontWeight: "bold", color: "#1976d2" }}
        >
          Contact Numbers ({contacts.length})
        </Typography>

        {contacts.length > 0 ? (
          <Paper variant="outlined" sx={{ p: 1, backgroundColor: "white" }}>
            <List dense>
              {contacts.map((contact, index) => (
                <ListItem
                  key={index}
                  sx={{
                    borderBottom: "1px solid #eee",
                    "&:last-child": { borderBottom: "none" },
                  }}
                  secondaryAction={
                    <Chip
                      label="Remove"
                      size="small"
                      onClick={() => removeContact(index)}
                      sx={{
                        backgroundColor: "#ffebee",
                        color: "#f44336",
                        "&:hover": { backgroundColor: "#ffcdd2" },
                      }}
                    />
                  }
                >
                  <ListItemText
                    primary={contact}
                    primaryTypographyProps={{
                      sx: {
                        fontFamily: "monospace",
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        ) : (
          <Typography
            variant="body2"
            sx={{ fontStyle: "italic", color: "#777", mt: 2 }}
          >
            No contact numbers added yet
          </Typography>
        )}
      </Paper>
      <AddToTargetBtn onClick={generateTargetAudience} />
    </Container>
  );
};

export default RdCampaignQuickContacts;
